/** Song page sections: detail (WS1), pipeline steps (spine), analysis (WS3), TikTok usage (WS4), linked videos. */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { StatusPill } from "../../components/StatusPill";
import { fmtMs } from "../../lib/frames";
import { useAction } from "../lib/actions";
import { BarChart, Sparkline } from "../lib/charts";
import { fmtDelta, fmtDuration, fmtNum, fmtPct, fmtWhen } from "../lib/time";
import { ActionError, Empty, QueryView, Section, TableScroll } from "../lib/ui";

type Song = Schemas["SongDetail"];

export function useSong(songId: number) {
  return useQuery({ queryKey: qk.entity("song", songId), queryFn: () => unwrap(api.GET("/api/songs/{song_id}", { params: { path: { song_id: songId } } })) });
}

export function DetailSection({ song }: { song: ReturnType<typeof useSong> }) {
  return (
    <Section id="detail" title="Song">
      <QueryView q={song} what="song details">
        {(s: Song) => {
          const pc = s.playcount_history.map((p) => p.playcount);
          const first = pc[0];
          const last = pc.at(-1);
          return (
            <dl className="ws7-kv" data-testid="song-detail">
              <dt>Artists</dt>
              <dd>
                <ul className="ws7-chips">
                  {s.artists.map((a, i) => (
                    <li key={`${a.name}-${i}`} className={`chip${a.role === "feat" ? " ws7-chip-feat" : ""}`}>{a.role === "feat" ? `${a.name} (feat)` : a.name}</li>
                  ))}
                </ul>
              </dd>
              <dt>Release</dt><dd>{s.release_date ?? "–"}</dd>
              <dt>Album</dt><dd>{s.album ?? "–"}</dd>
              <dt>Length</dt><dd>{fmtDuration(s.duration_ms) || "–"}</dd>
              <dt>Spotify plays</dt>
              <dd data-testid="song-playcount">
                {s.playcount != null ? fmtNum(s.playcount) : "unavailable"}
                {pc.length > 1 && first != null && last != null && (
                  <> <Sparkline values={pc} label={`Spotify play count: ${pc.length} samples from ${fmtNum(first)} to ${fmtNum(last)}`} />
                    {" "}<span className="ws7-muted">{fmtDelta(last - first)} since {fmtWhen(s.playcount_history[0]?.t)}</span></>
                )}
              </dd>
              <dt>Studio audio</dt><dd>{s.studio_audio_media_id != null ? `media #${s.studio_audio_media_id} (${s.studio_audio_source ?? "?"})` : "not yet"}</dd>
              {s.spotify_id && <><dt>Spotify id</dt><dd className="ws7-mono">{s.spotify_id}</dd></>}
              {s.is_feat && <><dt>Feat</dt><dd>yes</dd></>}
            </dl>
          );
        }}
      </QueryView>
    </Section>
  );
}

export function StepsSection({ entity, id }: { entity: string; id: number }) {
  const q = useQuery({ queryKey: qk.entity(entity, id, "steps"), queryFn: () => unwrap(api.GET("/api/steps/{entity}/{entity_id}", { params: { path: { entity, entity_id: id } } })) });
  const rerun = useAction(
    (step: string) => unwrap(api.POST("/api/steps/{entity}/{entity_id}/{step}/rerun", { params: { path: { entity, entity_id: id, step } } })),
    {
      label: (step) => `Rerun ${step}`,
      success: (r, step) => (r.enqueued.length
        ? `Rerun ${step}: queued job${r.enqueued.length === 1 ? "" : "s"} ${r.enqueued.map((j) => `#${j}`).join(", ")}`
        : `Rerun ${step}: marked stale, nothing queued yet${r.errors.length ? ` (${r.errors.join("; ")})` : ""}`),
      invalidate: [qk.entity(entity, id), ["jobs"]],
    },
  );
  return (
    <Section id="steps" title="Pipeline steps">
      <ActionError error={rerun.error} />
      <QueryView q={q} what="steps">
        {(steps) => (steps.length === 0 ? <Empty>No steps yet (the reconciler has not planned any work for this {entity}).</Empty> : (
          <TableScroll label="Steps table">
            <table className="ws7-table" aria-label="Pipeline steps" data-testid="steps-table">
              <thead><tr><th scope="col">Step</th><th scope="col">State</th><th scope="col">Progress</th><th scope="col">Message</th><th scope="col">Updated</th><th scope="col">Job</th><th scope="col">Actions</th></tr></thead>
              <tbody>
                {steps.map((s) => (
                  <tr key={s.step} data-testid={`step-${s.step}`}>
                    <td className="ws7-mono">{s.step}</td>
                    <td><StatusPill state={s.state} label={s.state === "queued" && s.queue_position != null ? `#${s.queue_position}` : undefined} /></td>
                    <td>{s.progress != null ? fmtPct(s.progress) : ""}</td>
                    <td>{s.message ?? ""}</td>
                    <td>{fmtWhen(s.updated_at)}</td>
                    <td>{s.job_id != null ? <Link to={`/jobs?job=${s.job_id}`}>job #{s.job_id}</Link> : ""}</td>
                    <td>
                      <button type="button" data-testid={`rerun-${s.step}`} aria-label={`Rerun ${s.step}`} disabled={rerun.pending} onClick={() => void rerun.run(s.step)}>
                        Rerun
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        ))}
      </QueryView>
    </Section>
  );
}

export function AnalysisSection({ songId }: { songId: number }) {
  const q = useQuery({ queryKey: qk.entity("song", songId, "analysis"), queryFn: () => unwrap(api.GET("/api/songs/{song_id}/analysis", { params: { path: { song_id: songId } } })) });
  return (
    <Section id="analysis" title="Analysis">
      <QueryView q={q} what="analysis">
        {(a) => (
          <>
            <p data-testid="analysis-summary">
              BPM <strong>{a.bpm != null ? a.bpm.toFixed(2) : "–"}</strong> ({a.beat_method ?? "?"}) · {a.beats_ms.length} beats · {a.downbeats_ms.length} downbeats ·
              sections {a.sections_locked ? "locked" : "automatic"} ({a.section_method ?? "?"}) · stems {a.stems ? a.stems.model : "not yet"}
            </p>
            {a.sections.length === 0 ? <Empty>No sections yet.</Empty> : (
              <TableScroll label="Sections table">
                <table className="ws7-table" aria-label="Sections" data-testid="sections-table">
                  <thead><tr><th scope="col">Section</th><th scope="col">Start</th><th scope="col">End</th><th scope="col">Performer</th><th scope="col">Confidence</th><th scope="col">Source</th></tr></thead>
                  <tbody>
                    {a.sections.map((s, i) => (
                      <tr key={`${s.label}-${i}`}><td>{s.label}</td><td className="ws7-mono">{fmtMs(s.t0_song_ms)}</td><td className="ws7-mono">{fmtMs(s.t1_song_ms)}</td>
                        <td>{s.performer ?? ""}</td><td>{s.conf != null ? s.conf.toFixed(2) : ""}</td><td>{s.source}</td></tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </>
        )}
      </QueryView>
    </Section>
  );
}

const SOUND_KINDS: Schemas["SoundKind"][] = ["catalog", "official_original", "teaser", "user_original", "unknown"];

export function TikTokSection({ songId }: { songId: number }) {
  const key = qk.entity("song", songId, "tiktok");
  const q = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/api/songs/{song_id}/tiktok", { params: { path: { song_id: songId }, query: { top: 20 } } })) });
  const [music, setMusic] = useState("");
  const [kind, setKind] = useState<Schemas["SoundKind"]>("unknown");
  const add = useAction(
    () => unwrap(api.POST("/api/songs/{song_id}/tiktok/sounds", { params: { path: { song_id: songId } }, body: { music: music.trim(), kind } })),
    { label: "Add sound", success: (s) => `Added TikTok sound ${s.music_id} (${s.kind})`, invalidate: [key, ["board"]] },
  );
  const harvest = useAction(
    () => unwrap(api.POST("/api/songs/{song_id}/tiktok/harvest", { params: { path: { song_id: songId } } })),
    { label: "Harvest", success: (r) => `TikTok harvest queued: jobs ${r.job_ids.map((j) => `#${j}`).join(", ") || "none"}`, invalidate: [key, ["jobs"]] },
  );
  return (
    <Section id="tiktok" title="TikTok usage"
      actions={<button type="button" data-testid="tiktok-harvest" disabled={harvest.pending} onClick={() => void harvest.run()}>Harvest now</button>}>
      <ActionError error={harvest.error} />
      <QueryView q={q} what="TikTok usage">
        {(t) => (
          <>
            <h3>Sounds</h3>
            {t.sounds.length === 0 ? <Empty>No sounds attached.</Empty> : (
              <TableScroll label="Sounds table">
                <table className="ws7-table" aria-label="TikTok sounds" data-testid="sounds-table">
                  <thead><tr><th scope="col">Sound</th><th scope="col">Kind</th><th scope="col" className="num">Videos</th><th scope="col" className="num">Items</th>
                    <th scope="col" className="num">Audio</th><th scope="col" className="num">Matched</th><th scope="col">Harvested</th></tr></thead>
                  <tbody>
                    {t.sounds.map((s) => (
                      <tr key={s.id}><td><a href={s.url} target="_blank" rel="noreferrer">{s.title ?? s.music_id}</a> <span className="ws7-muted">{s.author ?? ""}</span></td>
                        <td>{s.kind}</td><td className="num">{fmtNum(s.video_count)}</td><td className="num">{s.n_items}</td><td className="num">{s.n_audio}</td>
                        <td className="num">{s.n_matched}</td><td>{fmtWhen(s.harvested_at) || "never"}</td></tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
            <form className="ws7-toolbar" aria-label="Add TikTok sound" onSubmit={async (e) => {
              e.preventDefault();
              if (music.trim() && (await add.run())) setMusic("");
            }}>
              <label className="ws7-field">
                TikTok music URL or id
                <input value={music} data-testid="sound-music" onChange={(e) => setMusic(e.target.value)} />
              </label>
              <label className="ws7-field">
                Sound kind
                <select value={kind} data-testid="sound-kind" onChange={(e) => setKind(e.target.value as Schemas["SoundKind"])}>
                  {SOUND_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <button type="submit" data-testid="sound-add" disabled={music.trim().length < 5 || add.pending}>Add sound</button>
            </form>
            <ActionError error={add.error} />
            <h3>Hot windows</h3>
            {!t.usage || t.usage.hot_windows.length === 0 ? <Empty>No usage histogram yet.</Empty> : (
              <>
                <p className="ws7-muted" data-testid="usage-summary">{t.usage.n_matched} of {t.usage.n_items} items located · bins of {t.usage.bin_ms} ms · updated {fmtWhen(t.usage.updated_at)}</p>
                <BarChart rows={t.usage.hot_windows.map((w) => ({ label: `${fmtMs(w.t0_song_ms)}–${fmtMs(w.t1_song_ms)}`, value: Math.round(w.score * 100) }))}
                  fmt={(v) => `${v}%`} label={`Hot windows: ${t.usage.hot_windows.map((w) => `${fmtMs(w.t0_song_ms)} to ${fmtMs(w.t1_song_ms)} score ${w.score.toFixed(2)}`).join("; ")}`} />
                <table className="ws7-table" aria-label="Hot windows" data-testid="hot-windows">
                  <thead><tr><th scope="col">From</th><th scope="col">To</th><th scope="col">Score</th></tr></thead>
                  <tbody>{t.usage.hot_windows.map((w, i) => <tr key={i}><td className="ws7-mono">{fmtMs(w.t0_song_ms)}</td><td className="ws7-mono">{fmtMs(w.t1_song_ms)}</td><td>{w.score.toFixed(2)}</td></tr>)}</tbody>
                </table>
              </>
            )}
            <h3>Top items</h3>
            {t.top_items.length === 0 ? <Empty>No items harvested.</Empty> : (
              <TableScroll label="Top items table">
                <table className="ws7-table" aria-label="Top TikTok items" data-testid="top-items">
                  <thead><tr><th scope="col">Item</th><th scope="col" className="num">Plays</th><th scope="col" className="num">Likes</th><th scope="col">Created</th><th scope="col">Located in song</th></tr></thead>
                  <tbody>
                    {t.top_items.map((it) => (
                      <tr key={it.item_id}><td><a href={it.url} target="_blank" rel="noreferrer">{it.author ?? it.item_id}</a></td>
                        <td className="num">{fmtNum(it.plays)}</td><td className="num">{fmtNum(it.likes)}</td><td>{fmtWhen(it.created_at)}</td>
                        <td>{it.matches.map((m) => (m.status === "matched" ? `${fmtMs(m.t_song0_ms)}–${fmtMs(m.t_song1_ms)} ×${m.speed?.toFixed(2) ?? "1"}` : "unknown")).join("; ") || "–"}</td></tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </>
        )}
      </QueryView>
    </Section>
  );
}

export function LinkedVideos({ ids }: { ids: number[] }) {
  // titles from the spine's videos board (show_all) so this works before WS1's /api/videos lands
  const board = useQuery({
    queryKey: qk.board("videos", { show_all: true }),
    queryFn: () => unwrap(api.GET("/api/board/{group}", { params: { path: { group: "videos" }, query: { show_all: true } } })),
    enabled: ids.length > 0,
  });
  const titles = new Map((board.data?.items ?? []).map((i) => [i.id, i.title]));
  return (
    <Section id="videos" title="Linked videos">
      {ids.length === 0 ? <Empty>No music video linked yet.</Empty> : (
        <ul data-testid="linked-videos">
          {ids.map((id) => <li key={id}><Link to={`/watch/${id}`}>{titles.get(id) ?? `Video #${id}`}</Link></li>)}
        </ul>
      )}
    </Section>
  );
}
